<template>
  <div class="stripe">
    <h5 class="stripe__title">{{ copy_intro }}</h5>

    <div class="stripe__wrap">
      <loader v-if="loading" />

      <div v-show="!loading">
        <vb-input
          ref="ccname"
          v-model="name"
          :vb_options="{
            placeholder: 'Intestatario',
            disabled: card_confirmed,
          }"
        />
        <div ref="card" id="card" class="stripe__card" :class="{ 'stripe__card--disabled': card_confirmed }"></div>

        <vb-button
          class="stripe__btn"
          v-if="!card_confirmed"
          @click="handle_get_intent_client_secret()"
          :vb_options="{
            text: copy_button,
            disabled: !stripe_form_complete,
            size: 'sm',
            variant: 'outline-info',
          }"
        />

        <span
          v-else
          class="stripe__edit"
          @click="handle_edit_card()"
        >
          modifica carta
        </span>
      </div>
    </div>

    <div class="stripe__error" v-if="stripe_error !== ''">
      <b-alert show variant="danger" class="text-center">
        {{ stripe_error }}
      </b-alert>
    </div>
  </div>
</template>

<script>
import { service_handler } from '@/services'
import {
  get_initial_state,
  get_stripe_card_style,
  build_payment_method,
  format_confirm_success,
  format_confirm_error,
} from './logic'

export default {
  /*
    carta di credito test ok:       4242 4242 4242 4242 (data futura/CVC arbitrario)
    carta di credito test errore:   4000 0000 0000 0002 (data futura/CVC arbitrario)
  */
  name: 'stripe',

  emits: [
    'stripe-init',    // quando viene avviato il tutto
    'stripe-change',  // quando vengono inseriti i dati (emette lo status del form di inserimento)
    'stripe-confirm', // quando viene confermato o meno un pagamento
  ],

  props: {
    credit_card_amount: Number,
    copy_intro: {
      type: String,
      default: 'Inserisci la carta',
    },
    copy_button: {
      type: String,
      default: 'Conferma carta',
    },
    copy_success: {
      type: String,
      default: 'Puoi procedere con il checkout',
    },
    copy_error: {
      type: String,
      default: 'C\'è stato un problema',
    }
  },

  data() {
    return {
      loading: false,
      stripe_setup_public_key: this.$config.stripePublicKey,
      stripe: '',
      name: '',
      ...get_initial_state(),
    }
  },

  computed: {
    card_confirmed() {
      return this.stripe_payment_intent_id !== ''
    },
  },

  watch: {
    credit_card_amount(new_value, old_value) {
      if (new_value !== old_value) this.stripe_setup()
    }
  },

  mounted() {
    this.stripe_setup()
  },

  methods: {
    stripe_setup() {
      // Reset state
      Object.assign(this, get_initial_state())

      // Init Stripe SDK
      this.stripe = window.Stripe(this.stripe_setup_public_key)
      this.stripe_elements = this.stripe.elements()

      // Create card element with style
      this.stripe_card_element = this.stripe_elements.create('card', {
        hidePostalCode: true,
        style: get_stripe_card_style(this.sass),
      })

      // Mount and listen
      this.stripe_card_element.mount(this.stripe_card_selector)
      this.stripe_card_element.on('change', (event) => {
        this.$emit('change', { name: 'stripe-change', payload: event.complete })
        this.stripe_form_complete = event.complete
      })

      this.$emit('change', { name: 'stripe-init' })
    },

    handle_get_intent_client_secret() {
      this.loading = true
      service_handler('stripeCreateIntentClientSecret', {
        token: this.$config.token,
        amount: this.credit_card_amount,
      }).then((response) => {
        this.stripe_intent_client_secret = response.data.intentClientSecret
        this.handle_confirm_card_payment()
      })
    },

    handle_confirm_card_payment() {
      const payment_data = build_payment_method(this.stripe_card_element, this.name)

      this.stripe.confirmCardPayment(this.stripe_intent_client_secret, payment_data)
        .then((result) => {
          if (result.error) {
            this.stripe_error = result.error.message
            this.$emit('change', format_confirm_error(result.error))
            return
          }

          this.stripe_error = ''
          this.stripe_payment_intent_id = result.paymentIntent.id
          this.stripe_card_element.update({ disabled: true })
          this.$emit('change', format_confirm_success(this.stripe_payment_intent_id, this.name))
        })
        .catch((error) => {
          this.stripe_error = error.message || 'Errore durante il pagamento'
          this.$emit('change', format_confirm_error(error))
        })
        .finally(() => {
          this.loading = false
        })
    },

    handle_edit_card() {
      this.name = ''
      this.stripe_setup()
      this.$emit('change', { name: 'stripe-init' })
    },
  },
}
</script>

<style lang="scss">
  @import '~bootstrap/scss/_forms.scss';

  .stripe {

    &__title {
      @extend %sidebar__title;
    }

    &__wrap {
      text-align: right;
    }

    &__card {
      @extend .form-control;
      padding-top: 12px !important;
      margin: $grid-gutter-width / 3 0;

      &--disabled {
        background-color: $gray-100;
        pointer-events: none;
        filter: grayscale(1);
      }
    }

    &__btn {
      margin-top: $grid-gutter-width / 3;
    }

    &__edit {
      font-size: $font-size-md;
      text-decoration: underline;
      cursor: pointer;
      color: $info;
    }

    &__error {
      margin-top: $grid-gutter-20;
    }
  }
</style>
